import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Pin, PinOff } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { FOOTER_HEIGHT } from '@/lib/constants'
import { formatTime } from '@/lib/time-format'
import { useUISettingsStore } from '@/store/useUISettingsStore'

type FooterProps = {
  currentIndex: number
  totalImages: number
  onPrev?: () => void
  onNext?: () => void
  isVideoPlayer?: boolean
  subtitle?: string
  direction?: 'ttb' | 'rtl' | 'ltr'
  /** フッター固定のピンボタンを表示するか */
  pinnable?: boolean
}

export const Footer = ({
  currentIndex,
  totalImages,
  onPrev,
  onNext,
  isVideoPlayer,
  subtitle,
  direction = 'ltr',
  pinnable = false,
}: FooterProps) => {
  const isPinned = useUISettingsStore((state) => state.isReaderFooterPinned)
  const setPinned = useUISettingsStore((state) => state.setReaderFooterPinned)
  const isTTB = direction === 'ttb'
  const PrevIcon = isTTB ? ChevronUp : ChevronLeft
  const NextIcon = isTTB ? ChevronDown : ChevronRight
  // 全体に対する進捗率。0 除算を避け、ページ表示時のみ算出する
  const progressPercent = !isVideoPlayer && totalImages > 0 ? ((currentIndex + 1) / totalImages) * 100 : null

  return (
    <div
      className="relative bg-background border-t border-gray-600 z-10"
      style={{
        height: FOOTER_HEIGHT,
      }}
    >
      <div className="flex items-start justify-between h-full px-4">
        <div className="flex items-center gap-2 pt-2">
          <span className="text-white text-sm font-mono">
            {isVideoPlayer
              ? `${formatTime(currentIndex)} / ${formatTime(totalImages)}`
              : `${currentIndex + 1}/${totalImages}`}
            {subtitle && ` (${subtitle})`}
          </span>
          {progressPercent !== null && (
            <span className="text-muted-foreground text-sm font-mono">{progressPercent.toFixed(1)}%</span>
          )}
          {pinnable && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPinned(!isPinned)}
              aria-pressed={isPinned}
              title={isPinned ? 'フッター固定を解除' : 'フッターを常に表示'}
              aria-label={isPinned ? 'フッター固定を解除' : 'フッターを常に表示'}
              className={`w-auto! px-0! ${isPinned ? 'text-primary' : 'text-muted-foreground'}`}
            >
              {isPinned ? <Pin size={18} /> : <PinOff size={18} />}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-20">
            {onPrev && (
              <Button
                variant="ghost"
                onClick={onPrev}
                className="text-white flex items-center gap-0 [&_svg]:size-6! w-full justify-start"
              >
                <PrevIcon />
                Prev
              </Button>
            )}
          </div>
          <div className="w-20">
            {onNext && (
              <Button
                variant="ghost"
                onClick={onNext}
                className="text-white flex items-center gap-0 [&_svg]:size-6! w-full justify-end"
              >
                Next
                <NextIcon />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
